import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { BrandIcon } from 'components/brand/BrandIcon';
import { FormSelect } from 'components/form/FormSelect';
import { connectGivebutter } from 'lib/connect';
import { DecodedInstance, createApi } from 'lib/gb-api';

type Api = ReturnType<typeof createApi>;

interface AccountGateProps {
  api: Api;
  instance: DecodedInstance;
}

interface PickerValues {
  account_id: number | '';
}

export const AccountGate = ({ api, instance }: AccountGateProps) => {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);

  const connectMutation = useMutation({
    mutationFn: () => connectGivebutter(api),
    onSettled: () => {
      setConnecting(false);
      queryClient.invalidateQueries({ queryKey: ['wix', 'instance'] });
    },
  });

  if (!instance.is_connected) {
    return (
      <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <BrandIcon size={80} />

        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-gray-900">
            Let&rsquo;s connect Givebutter 👋
          </h1>
          <p className="text-sm leading-relaxed text-gray-500">
            Link your Givebutter account to add and manage donation widgets right from your Wix
            site.
          </p>
        </div>

        <Button
          variant="solid"
          color="primary"
          size="lg"
          className="w-full justify-center"
          disabled={connecting}
          onClick={() => {
            setConnecting(true);
            connectMutation.mutate();
          }}
        >
          {connecting ? 'Waiting for Givebutter…' : 'Connect Givebutter account'}
        </Button>

        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700">
          🔒 You&rsquo;ll sign in securely with Givebutter
        </span>

        <img
          src="/brand/mrbutter.png"
          alt=""
          aria-hidden="true"
          className="mt-1 h-28 w-auto select-none"
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md p-2">
      <div className="mb-5 flex flex-col items-center gap-4 text-center">
        <BrandIcon size={56} />
        <div className="flex flex-col gap-1.5">
          <h1 className="text-xl font-semibold text-gray-900">Choose your account</h1>
          <p className="text-sm text-gray-500">Pick the Givebutter account to use on this site.</p>
        </div>
      </div>
      <AccountPicker api={api} selectedAccountId={instance.account_id} />
    </div>
  );
};

interface AccountPickerProps {
  api: Api;
  selectedAccountId: number | null;
  onSuccess?: () => void;
}

export const AccountPicker = ({ api, selectedAccountId, onSuccess }: AccountPickerProps) => {
  const queryClient = useQueryClient();

  const accountsQuery = useQuery({
    queryKey: ['wix', 'accounts'],
    queryFn: api.listAccounts,
  });

  const { control, watch } = useForm<PickerValues>({
    defaultValues: { account_id: selectedAccountId ?? '' },
  });

  const chosen = watch('account_id');

  const selectMutation = useMutation({
    mutationFn: (accountId: number) => api.selectAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wix', 'instance'] });
      queryClient.invalidateQueries({ queryKey: ['wix', 'elements'] });
      queryClient.invalidateQueries({ queryKey: ['wix', 'campaigns'] });
      onSuccess?.();
    },
  });

  const options = useMemo(
    () => (accountsQuery.data?.accounts ?? []).map(a => ({ value: String(a.id), label: a.name })),
    [accountsQuery.data?.accounts],
  );

  return (
    <div className="flex flex-col gap-4">
      <FormSelect
        name="account_id"
        control={control}
        label="Account"
        placeholder={accountsQuery.isLoading ? 'Loading…' : 'Select an account…'}
        options={options}
      />
      <Button
        variant="solid"
        color="primary"
        size="lg"
        className="w-full justify-center"
        disabled={chosen === '' || Number(chosen) === selectedAccountId || selectMutation.isPending}
        onClick={() => chosen !== '' && selectMutation.mutate(Number(chosen))}
      >
        {selectMutation.isPending ? 'Saving…' : 'Use this account'}
      </Button>
    </div>
  );
};
